import React, { useRef, useState } from "react";

// shade a hex color by percent (-100..100)
const shade = (hex, p) => {
  let c = (hex || "#1E40AF").replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const num = parseInt(c, 16);
  let r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  const t = p < 0 ? 0 : 255;
  const a = Math.abs(p) / 100;
  r = Math.round((t - r) * a) + r;
  g = Math.round((t - g) * a) + g;
  b = Math.round((t - b) * a) + b;
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

// Realistic jersey body with rounded shoulders, tapered waist and curved hem
const TEE =
  "M118,64 C120,54 136,48 160,48 C184,48 200,54 202,64 " +
  "L246,80 C258,90 268,108 272,128 L238,150 C234,140 228,132 222,128 " +
  "C221,180 221,270 224,304 C225,318 214,326 200,326 " +
  "L120,326 C106,326 95,318 96,304 C99,270 99,180 98,128 " +
  "C92,132 86,140 82,150 L48,128 C52,108 62,90 74,80 Z";

// Left & right sleeve caps (for separate shading)
const SLEEVE_L = "M118,64 L82,150 L48,128 C52,108 62,90 74,80 Z";
const SLEEVE_R = "M202,64 L238,150 L272,128 C268,108 258,90 246,80 Z";

const SHORTS =
  "M108,344 L212,344 C214,344 215,346 215,348 L206,430 C205,434 202,436 198,436 " +
  "L172,436 C169,436 167,434 166,430 L161,384 C160,380 160,380 159,384 L154,430 " +
  "C153,434 151,436 148,436 L122,436 C118,436 115,434 114,430 L105,348 C105,346 106,344 108,344 Z";

const TshirtDesigner = ({ design, side, svgId = "design-svg", interactive = false, onChange }) => {
  const { color, secondary, pattern } = design;
  const s = design[side];
  const svgRef = useRef(null);
  const [sel, setSel] = useState(null); // 'logo' | 'text'
  const drag = useRef(null);

  const base = color || "#1E40AF";
  const hi = shade(base, 26);      // lit highlight
  const mid = base;                // mid tone
  const dk = shade(base, -22);     // shadow toward edges
  const edge = shade(base, -40);   // outline / deep shadow
  const sHi = shade(base, 16), sDk = shade(base, -26);

  const toLocal = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  };

  const startDrag = (which) => (e) => {
    if (!interactive) return;
    e.stopPropagation();
    setSel(which);
    const p = toLocal(e);
    drag.current = { which, ox: p.x, oy: p.y, sx: which === "logo" ? s.logoX : s.textX, sy: which === "logo" ? s.logoY : s.textY };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const p = toLocal(e);
    const dx = p.x - drag.current.ox, dy = p.y - drag.current.oy;
    const nx = Math.max(50, Math.min(270, drag.current.sx + dx));
    const ny = Math.max(70, Math.min(430, drag.current.sy + dy));
    onChange(drag.current.which === "logo" ? "logoX" : "textX", Math.round(nx));
    onChange(drag.current.which === "logo" ? "logoY" : "textY", Math.round(ny));
  };
  const onUp = () => {
    drag.current = null;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
  };

  const stripes = [];
  if (pattern === "stripes") for (let x = 90; x < 232; x += 24) stripes.push(<rect key={x} x={x} y={44} width={12} height={290} fill={secondary} />);

  return (
    <svg id={svgId} ref={svgRef} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 470" width="320" height="470"
      className="w-full h-auto select-none" onPointerDown={() => interactive && setSel(null)}>
      <defs>
        {/* body: side ambient occlusion -> lit center -> ao */}
        <linearGradient id={`${svgId}-body`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={edge} />
          <stop offset="14%" stopColor={dk} />
          <stop offset="42%" stopColor={hi} />
          <stop offset="58%" stopColor={hi} />
          <stop offset="86%" stopColor={dk} />
          <stop offset="100%" stopColor={edge} />
        </linearGradient>
        <linearGradient id={`${svgId}-vert`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.22" />
          <stop offset="20%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="80%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.18" />
        </linearGradient>
        <linearGradient id={`${svgId}-slL`} x1="0" y1="0" x2="1" y2="0.4">
          <stop offset="0%" stopColor={edge} /><stop offset="100%" stopColor={mid} />
        </linearGradient>
        <linearGradient id={`${svgId}-slR`} x1="1" y1="0" x2="0" y2="0.4">
          <stop offset="0%" stopColor={edge} /><stop offset="100%" stopColor={mid} />
        </linearGradient>
        <linearGradient id={`${svgId}-shorts`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={sDk} /><stop offset="18%" stopColor={shade(base, -8)} />
          <stop offset="50%" stopColor={sHi} /><stop offset="82%" stopColor={shade(base, -8)} /><stop offset="100%" stopColor={sDk} />
        </linearGradient>
        <radialGradient id={`${svgId}-glow`} cx="50%" cy="26%" r="60%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.40" />
          <stop offset="70%" stopColor="#ffffff" stopOpacity="0.04" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <clipPath id={`${svgId}-clip`}><path d={TEE} /></clipPath>
        <filter id={`${svgId}-shadow`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="9" floodColor="#0f172a" floodOpacity="0.28" />
        </filter>
      </defs>

      <rect width="320" height="470" fill="#eef1f5" />
      <ellipse cx="160" cy="452" rx="96" ry="12" fill="#000000" opacity="0.10" />

      {/* SHORTS (drawn first, behind hem) */}
      <g filter={`url(#${svgId}-shadow)`}>
        <path d={SHORTS} fill={`url(#${svgId}-shorts)`} stroke={edge} strokeWidth="1.6" />
        <rect x="106" y="340" width="108" height="12" rx="4" fill={sDk} />
        <rect x="106" y="340" width="108" height="4" rx="2" fill={secondary} opacity="0.85" />
        <path d="M160,352 L160,436" stroke={shade(base, -36)} strokeWidth="2" opacity="0.45" />
        <path d="M120,360 Q116,400 124,430" stroke="#000" strokeWidth="2" opacity="0.10" fill="none" />
        <path d="M200,360 Q204,400 196,430" stroke="#000" strokeWidth="2" opacity="0.10" fill="none" />
      </g>

      {/* SLEEVES */}
      <path d={SLEEVE_L} fill={`url(#${svgId}-slL)`} stroke={edge} strokeWidth="1.4" />
      <path d={SLEEVE_R} fill={`url(#${svgId}-slR)`} stroke={edge} strokeWidth="1.4" />
      {/* sleeve cuffs */}
      <path d="M74,80 L48,128 L58,134 L84,88 Z" fill={shade(base, -32)} />
      <path d="M246,80 L272,128 L262,134 L236,88 Z" fill={shade(base, -32)} />
      {(pattern === "raglan") && (<>
        <path d={SLEEVE_L} fill={secondary} opacity="0.9" />
        <path d={SLEEVE_R} fill={secondary} opacity="0.9" />
      </>)}

      {/* TEE body */}
      <g filter={`url(#${svgId}-shadow)`}>
        <path d={TEE} fill={`url(#${svgId}-body)`} />
        <g clipPath={`url(#${svgId}-clip)`}>
          {pattern === "stripes" && stripes}
          <path d={TEE} fill={`url(#${svgId}-vert)`} />
          <ellipse cx="160" cy="140" rx="78" ry="96" fill={`url(#${svgId}-glow)`} />
          {/* soft fabric folds */}
          <path d="M132,150 Q150,240 134,318" stroke="#000" strokeWidth="2" opacity="0.07" fill="none" />
          <path d="M188,150 Q170,240 186,318" stroke="#000" strokeWidth="2" opacity="0.07" fill="none" />
          <path d="M160,120 Q158,230 160,318" stroke="#000" strokeWidth="1.5" opacity="0.05" fill="none" />
          {/* side seam shadows */}
          <path d="M100,130 L108,130 L110,318 L100,314 Z" fill="#000" opacity="0.10" />
          <path d="M220,130 L212,130 L210,318 L220,314 Z" fill="#000" opacity="0.10" />
        </g>
        <path d={TEE} fill="none" stroke={edge} strokeWidth="1.6" />
      </g>

      {/* COLLAR */}
      {side === "front" ? (
        <g>
          <path d="M134,50 C142,72 178,72 186,50 C176,64 144,64 134,50 Z" fill={shade(base, -34)} />
          <path d="M138,52 C146,68 174,68 182,52" fill="none" stroke={secondary} strokeWidth="3" opacity="0.9" />
        </g>
      ) : (
        <g>
          <path d="M134,50 C144,60 176,60 186,50 C176,58 144,58 134,50 Z" fill={shade(base, -34)} />
          <rect x="146" y="50" width="28" height="7" rx="3" fill={secondary} />
        </g>
      )}

      {/* Draggable LOGO */}
      {s.logo ? (
        <g onPointerDown={startDrag("logo")} style={{ cursor: interactive ? "move" : "default" }}>
          <image href={s.logo} x={s.logoX - s.logoSize / 2} y={s.logoY - s.logoSize / 2} width={s.logoSize} height={s.logoSize} preserveAspectRatio="xMidYMid meet" />
          {sel === "logo" && interactive && (
            <rect x={s.logoX - s.logoSize / 2} y={s.logoY - s.logoSize / 2} width={s.logoSize} height={s.logoSize} fill="none" stroke="#C9A227" strokeDasharray="4 3" strokeWidth="1.5" rx="4" />
          )}
        </g>
      ) : null}

      {/* Draggable TEXT */}
      {s.text ? (
        <g onPointerDown={startDrag("text")} style={{ cursor: interactive ? "move" : "default" }}>
          {sel === "text" && interactive && (
            <rect x={s.textX - s.text.length * s.textSize * 0.32 - 6} y={s.textY - s.textSize} width={s.text.length * s.textSize * 0.64 + 12} height={s.textSize * 1.35} fill="none" stroke="#C9A227" strokeDasharray="4 3" strokeWidth="1.5" rx="4" />
          )}
          <text x={s.textX} y={s.textY} textAnchor="middle" fontFamily="Montserrat, sans-serif" fontWeight="800" fontSize={s.textSize} fill={s.textColor} stroke="#00000033" strokeWidth="0.6" style={{ paintOrder: "stroke" }}>{s.text}</text>
        </g>
      ) : null}
    </svg>
  );
};

export default TshirtDesigner;
